import { consecutiveHourlyWindows, uniqueOrderedHours } from "../conditions/hourly-windows";
import {
  scoreSailing,
  type BoatCategory,
  type HourlyDatum,
  type SailingScore,
  type VenueType,
} from "../conditions/sailing-scoring";
import {
  storedOceanCurrentMs,
  type NormalisedHour,
} from "../conditions/daily-obs-types";
import type { NativeWindowScore, ScoreHour } from "./api-types";
import { comfortVerdict, type SessionContext } from "./comfort";
import { prefAbility } from "./preferences";
import { SCORE_THRESHOLD } from "./windows";
import type { DayOffset } from "./types";
import type {
  SessionHour,
  SessionPreferences,
  SessionWindow,
  WindowLabel,
} from "./types";

export type CanonicalSailingWindow = NativeWindowScore & {
  canonical: SailingScore;
  hours: readonly HourlyDatum[];
};

const RACING_START = 9;
const RACING_END = 18; // inclusive
const WINDOW = 4;

/**
 * Map stored hourly observations to the sailing scorer's input shape.
 */
export function toSailingHourly(hours: NormalisedHour[]): HourlyDatum[] {
  return hours.map((h) => {
    const w = h.weather;
    const m = h.marine;
    return {
      hour: h.hour,
      wind_speed_10m_kmh: w.wind_speed_10m_kmh ?? 0,
      wind_dir_10m_deg: w.wind_dir_10m_deg ?? 0,
      wind_gust_10m_kmh: w.wind_gust_10m_kmh ?? 0,
      wind_speed_80m_kmh: w.wind_speed_80m_kmh ?? null,
      wind_dir_80m_deg: w.wind_dir_80m_deg ?? null,
      temp_c: w.temp_c ?? 15,
      apparent_temp_c: w.apparent_temp_c ?? 15,
      humidity_pct: w.humidity_pct ?? 60,
      rain_mm: w.rain_mm ?? w.precip_mm ?? 0,
      precip_mm: w.precip_mm ?? 0,
      precip_probability_pct: w.precip_probability_pct ?? null,
      weather_code: w.weather_code ?? 0,
      pressure_msl_hpa: w.pressure_msl_hpa ?? 1013,
      visibility_m: w.visibility_m ?? 10000,
      cloud_cover_pct: w.cloud_cover_pct ?? 0,
      is_day: w.is_day ? 1 : 0,
      cape_jkg: w.cape_jkg ?? 0,
      lifted_index: w.lifted_index ?? 5,
      wave_height_m: m?.wave_height_m ?? null,
      wind_wave_height_m: m?.wind_wave_height_m ?? null,
      wind_wave_dir_deg: m?.wind_wave_dir_deg ?? null,
      wind_wave_period_s: m?.wind_wave_period_s ?? null,
      swell_height_m: m?.swell_height_m ?? null,
      swell_dir_deg: m?.swell_dir_deg ?? null,
      swell_period_s: m?.swell_period_s ?? null,
      swell_peak_period_s: m?.swell_peak_period_s ?? null,
      ocean_current_velocity_ms: storedOceanCurrentMs(m),
      ocean_current_dir_deg: m?.ocean_current_dir_deg ?? null,
      sst_c: m?.sst_c ?? null,
      source_visibility_m: w.visibility_m,
      source_wave_height_m: m?.wind_wave_height_m ?? m?.wave_height_m ?? null,
      source_pressure_msl_hpa: w.pressure_msl_hpa,
      source_weather_code: w.weather_code,
      source_cape_jkg: w.cape_jkg,
      source_lifted_index: w.lifted_index,
    };
  });
}

export function scoreSailingCandidateWindows(
  days: { offset: DayOffset; hourly: HourlyDatum[] }[],
  category: BoatCategory,
  venueType: string | null,
  exposedSectors?: [number, number][]
): NativeWindowScore[] {
  return scoreSailingCandidateWindowsWithFactors(days, category, venueType, exposedSectors)
    .map(({ canonical: _canonical, hours: _hours, ...window }) => window);
}

export function scoreSailingCandidateWindowsWithFactors(
  days: { offset: DayOffset; hourly: HourlyDatum[] }[],
  category: BoatCategory,
  venueType: string | null,
  exposedSectors?: [number, number][]
): CanonicalSailingWindow[] {
  const vt = (venueType ?? "inland") as VenueType;
  const out: CanonicalSailingWindow[] = [];

  for (const day of days) {
    const daylight = uniqueOrderedHours(day.hourly).filter((hour) => hour.is_day === 1);
    const racing = daylight.filter((hour) => hour.hour >= RACING_START && hour.hour <= RACING_END);
    const pool = racing.length >= WINDOW ? racing : daylight;
    const duration = Math.min(WINDOW, pool.length);
    if (duration < 2) continue;
    for (const slice of consecutiveHourlyWindows(pool, duration)) {
      const start = slice[0].hour;
      const res = scoreSailing(slice, category, vt, exposedSectors);
      const sc = Math.round((res.score / 10) * 10) / 10; // 0-10, one decimal
      const safetyState = res.safety.some((item) => item.level === "red")
        ? "flagged-red" as const
        : res.safety.some((item) => item.level === "amber")
          ? "flagged-amber" as const
          : "no-data" as const;
      out.push({
        dayOffset: day.offset,
        startHour: start,
        endHour: start + duration,
        score: sc,
        safetyState,
        safetyNotes: res.safety.map((item) => item.label),
        canonical: res,
        hours: slice,
      });
    }
  }

  return out;
}

export function sailingHourlyCurveFromWindows(
  windows: NativeWindowScore[]
): ScoreHour[] {
  const scoreByHour = new Map<string, ScoreHour>();

  for (const window of windows) {
    for (let hour = window.startHour; hour < window.endHour; hour++) {
      const key = `${window.dayOffset}:${hour}`;
      const existing = scoreByHour.get(key);
      if (!existing || window.score > existing.score) {
        scoreByHour.set(key, {
          hour,
          dayOffset: window.dayOffset,
          score: window.score,
          ...(window.safetyState ? { safetyState: window.safetyState } : {}),
          ...(window.safetyNotes ? { safetyNotes: [...window.safetyNotes] } : {}),
        });
      }
    }
  }

  return [...scoreByHour.values()].sort(
    (a, b) => a.dayOffset - b.dayOffset || a.hour - b.hour
  );
}

/**
 * Synthesise an hourly sailing score curve by scoring every 4-hour racing
 * window and attributing each window's score (0-10) to its hours. The curve is
 * for the timeline only; recommendations should use the native window list.
 */
export function synthesiseSailingScores(
  days: { offset: DayOffset; hourly: HourlyDatum[] }[],
  category: BoatCategory,
  venueType: string | null,
  exposedSectors?: [number, number][]
): ScoreHour[] {
  return sailingHourlyCurveFromWindows(
    scoreSailingCandidateWindows(days, category, venueType, exposedSectors)
  );
}

type SailingWindowCategory = "ok" | "marginal" | "too-strong" | "too-weak";

function pad(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}

function mean(xs: number[]): number {
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

function fitScoreFor(score: number, fits: number[]): number {
  return Math.max(0, Math.min(100, score * 10 * mean(fits)));
}

function categoryForNativeWindow(
  score: number,
  threshold: number,
  verdicts: ReturnType<typeof comfortVerdict>[]
): SailingWindowCategory | null {
  if (
    verdicts.some((v) =>
      v.excludedBy === "timing" ||
      v.excludedBy === "water temp" ||
      v.excludedBy === "water quality"
    )
  ) {
    return null;
  }
  if (verdicts.some((v) => v.band === "too-strong")) return "too-strong";
  if (verdicts.some((v) => v.band === "too-weak")) return "too-weak";
  return score >= threshold ? "ok" : "marginal";
}

export function buildSailingWindows<THour extends SessionHour>(
  hours: readonly THour[],
  prefs: SessionPreferences,
  ctx: SessionContext,
  nativeWindows: readonly NativeWindowScore[]
): SessionWindow<THour>[] {
  const threshold = SCORE_THRESHOLD[prefAbility(prefs)];
  const hoursByKey = new Map<string, THour>();
  for (const h of hours) {
    if (h.isDay) hoursByKey.set(`${h.dayOffset}:${h.hour}`, h);
  }

  const candidates: Array<{
    native: NativeWindowScore;
    hours: THour[];
    category: SailingWindowCategory;
    fits: number[];
    fitScore: number;
  }> = [];

  for (const native of nativeWindows) {
    const windowHours: THour[] = [];
    for (let hour = native.startHour; hour < native.endHour; hour++) {
      const h = hoursByKey.get(`${native.dayOffset}:${hour}`);
      if (h) windowHours.push(h);
    }
    if (windowHours.length !== native.endHour - native.startHour) continue;

    // A window assessed from the engine's hourly v2 levels decides alone: a red
    // hour makes the window red, so its "no-data" hours are known not to be
    // red, and a clear window is never dropped for a red hour elsewhere in the
    // day. Only a window without that assessment falls back to today's day-level
    // red (revision 1 windows read "no-data" when clean).
    const assessed = native.safetyPolicy === "v2";
    const unavailable = native.safetyState === undefined || native.safetyState === "no-data";
    if (native.safetyState === "flagged-red") continue;
    if (!assessed && native.dayOffset === 0 && unavailable && ctx.safetyState === "flagged-red") continue;

    // Assessed windows keep each hour's own level (the hours carry it from the
    // stored curve); revision 1 amber windows mark every hour with the window.
    const effectiveHours: THour[] = !assessed && native.safetyState === "flagged-amber"
      ? windowHours.map((hour) => ({
          ...hour,
          safetyState: "flagged-amber" as const,
          safetyNotes: [...(native.safetyNotes ?? [])],
        }))
      : windowHours;

    const verdicts = effectiveHours.map((h) => comfortVerdict(prefs, h, ctx));
    const category = categoryForNativeWindow(native.score, threshold, verdicts);
    if (!category) continue;

    const fits = verdicts.map((v) => v.fitMultiplier);
    candidates.push({
      native,
      hours: effectiveHours,
      category,
      fits,
      fitScore: fitScoreFor(native.score, fits),
    });
  }

  const okCandidates = candidates
    .filter((c) => c.category === "ok")
    .slice()
    .sort((a, b) => b.fitScore - a.fitScore);
  const rankLabel = new Map<NativeWindowScore, WindowLabel>();
  okCandidates.forEach((c, i) => {
    rankLabel.set(c.native, i === 0 ? "best" : i === 1 ? "good" : "possible");
  });

  const windows = candidates.map((c): SessionWindow<THour> => {
    const label: WindowLabel =
      c.category === "ok"
        ? (rankLabel.get(c.native) ?? "possible")
        : c.category === "marginal"
          ? "not-recommended"
          : c.category === "too-strong"
            ? "too-strong"
            : "too-weak";

    return {
      dayOffset: c.native.dayOffset,
      dayLabel: c.native.dayOffset === 0 ? "Today" : "Tomorrow",
      startHour: c.native.startHour,
      endHour: c.native.endHour,
      timeLabel: `${pad(c.native.startHour)} - ${pad(c.native.endHour)}`,
      peakStartHour: c.native.startHour,
      peakEndHour: c.native.endHour,
      peakScore: c.native.score,
      label,
      avgScore: c.native.score,
      fitScore: c.fitScore,
      hours: c.hours,
      headline: "",
      explanation: "",
      warnings: [],
    };
  });

  return windows.sort((a, b) => a.dayOffset - b.dayOffset || a.startHour - b.startHour);
}
